import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMechanizationVarieties,
  getMechanizations,
  getSeasonsProcess,
  getSpeedChangesChartData,
  verifyPhoneNumber,
  getMechanizationMovementChanges,
  getMechanizationFuelConsumptionChanges,
  getMechanizationTemperatureChanges,
  getMechanization,
  getMechanizationQuestion,
  createMechanization,
  getMechanizationSensors,
  getCroppingPatterns,
  getExperimentsList,
  getPlantsTypeList,
  getProductsTypeList,
  createCroppingPattern,
  getCroppingSuitabilities,
  createCroppingSuitabilities,
  getCroppingPattern,
  getCroppingSuitability,
  deleteMechanization,
  postMechanizationDeviceCode,
  updateMechanizationDeviceCode,
  deleteMechanizationDeviceCode,
  postIrrigationSourceDeviceCode,
  updateIrrigationSourceDeviceCode,
  deleteIrrigationSourceDeviceCode,
  getIrrigationSensors,
  getEngineVibrationLevelChanges,
  getStatePowerChanges,
  getStatePumpChanges,
  getConsumptionApmsChanges,
  getIrrigationSource,
} from "@new-services/services/queries";
import {
  croppingPatternQueryKeys,
  croppingSuitabilityQueryKeys,
  irrigationSourceQueryKeys,
  mechanizationQueryKeys,
} from "@new-services/services/queryKeys";

import {
  QUERY_KEYS_CROPPING_PATTERN,
  QUERY_KEYS_CROPPING_SUITABILITY,
  QUERY_KEYS_EXPERIMENT,
  QUERY_KEYS_IRIIGATION_SOURCE,
  QUERY_KEYS_MECHANIZATION,
  QUERY_KEYS_PRODUCTS,
  QUERY_KEYS_STATICS,
} from "@new-services/services/enum";

import { toast } from "react-toastify";
import { ApiTypes } from "@agroyaar/sdk";
import { queryClient } from "@new-services/lib/queryClient";

export const useMechanizationVarieties = (
  variables: ApiTypes.Varieties["RequestParams"]["getAll"]
) =>
  useQuery({
    queryKey: mechanizationQueryKeys.varieties(variables.params.kindName),
    queryFn: () => getMechanizationVarieties(variables),
  });

export const useMechanization = (
  variables: ApiTypes.Mechanization["RequestParams"]["getOne"]
) =>
  useQuery({
    queryKey: mechanizationQueryKeys.mechanization(variables.pathParams),
    queryFn: () => getMechanization(variables),
  });

export const useCreateMechanization = (handleOnSuccess: () => void) => {
  return useMutation({
    mutationFn: (
      variables: ApiTypes.Mechanization["RequestParams"]["create"]
    ) => createMechanization(variables),
    onSuccess: () => {
      handleOnSuccess();
      toast.success(" با موفقیت ثبت شد");
    },
    onError: (e) => {
      toast.error("خطا در ثبت مکانیزاسیون");
    },
  });
};

export const useDeleteMechanization = () => {
  return useMutation({
    mutationFn: (
      variables: ApiTypes.Mechanization["RequestParams"]["delete"]
    ) => deleteMechanization(variables),
    onSuccess: () => {
      queryClient.invalidateQueries([
        QUERY_KEYS_MECHANIZATION.getMechanizations,
      ]);
      toast.success(" با موفقیت حذف شد");
    },
    onError: (e) => {
      console.log("error", e);
      toast.error("خطا در خدف مکانیزاسیون");
    },
  });
};

export const useMechanizations = (
  params: ApiTypes.Mechanization["RequestParams"]["getAll"]
) =>
  useQuery({
    queryKey: mechanizationQueryKeys.mechanizations(params),
    queryFn: () => getMechanizations(params),
    refetchOnMount: "always",
  });

export const useSeasonsProcess = () =>
  useQuery({
    queryFn: () => getSeasonsProcess(),
  });

export const useMechanizationSensors = (
  params: ApiTypes.MechanizationSensor["RequestParams"]["getSensors"],
  enabled: boolean
) =>
  useQuery({
    queryKey: mechanizationQueryKeys.sensors(params),
    queryFn: () => getMechanizationSensors(params),
    enabled,
  });

export const useSpeedChangesChart = (
  params: ApiTypes.MechanizationSensor["RequestParams"]["getSpeedChange"]
) =>
  useQuery({
    queryKey: mechanizationQueryKeys.speedChanges(params),
    queryFn: () => getSpeedChangesChartData(params),
  });

export const useMechanizationMovementChanges = (
  params: ApiTypes.MechanizationSensor["RequestParams"]["getMovementChange"],
  enabled: boolean
) =>
  useQuery({
    queryKey: mechanizationQueryKeys.movementChange(params),
    queryFn: () => getMechanizationMovementChanges(params),
    enabled,
  });

export const useMechanizationTemperatureChanges = (
  params: ApiTypes.MechanizationSensor["RequestParams"]["getTemperatureChanges"]
) =>
  useQuery({
    queryKey: mechanizationQueryKeys.temperatureChanges(params),
    queryFn: () => getMechanizationTemperatureChanges(params),
  });

export const useMechanizationFuelConsumptionChanges = (
  params: ApiTypes.MechanizationSensor["RequestParams"]["getFuelConsumptionChanges"]
) =>
  useQuery({
    queryKey: mechanizationQueryKeys.fuelConsumptionChange(params),
    queryFn: () => getMechanizationFuelConsumptionChanges(params),
  });

export const usePostMechanizationDeviceCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [QUERY_KEYS_MECHANIZATION.postTrackerDeviceCode],
    mutationFn: (params: ApiTypes.MechanizationDeviceCode["RequestParams"]["create"]) =>
      postMechanizationDeviceCode(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS_MECHANIZATION.getMechanization],
      });
    },
  });
};

export const useUpdateMechanizationDeviceCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [QUERY_KEYS_MECHANIZATION.updateTrackerDeviceCode],
    mutationFn: (params: ApiTypes.MechanizationDeviceCode["RequestParams"]["update"]) =>
      updateMechanizationDeviceCode(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS_MECHANIZATION.getMechanization],
      });
    },
  });
};

export const useDeleteMechanizationDeviceCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [QUERY_KEYS_MECHANIZATION.deleteTrackerDeviceCode],
    mutationFn: (params: ApiTypes.MechanizationDeviceCode["RequestParams"]["delete"]) =>
      deleteMechanizationDeviceCode(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS_MECHANIZATION.getMechanization],
      });
    },
  });
};

export const useVerifyPhoneNumber = () =>
  useMutation({
    mutationKey: [QUERY_KEYS_MECHANIZATION.verifyPhoneNumber],
    mutationFn: (
      params: ApiTypes.Auth["RequestParams"]["createVerifyPhoneNumber"]
    ) => verifyPhoneNumber(params),
    onError: () => {
      toast.error("کد تایید نامعتبر است ");
    },
  });

export const useMechanizationsQuestionList = (
  variables: ApiTypes.MechanizationQuestion["RequestParams"]["getAll"]
) =>
  useQuery({
    queryKey: [QUERY_KEYS_MECHANIZATION.getQuestions],
    queryFn: () => getMechanizationQuestion(variables),
    cacheTime: 0,
  });

export const useCroppingPatterns = (
  variables: ApiTypes.CroppingPattern["RequestParams"]["getAll"]
) =>
  useQuery({
    queryKey: [QUERY_KEYS_CROPPING_PATTERN.getCroppingPatterns],
    queryFn: () => getCroppingPatterns(variables),
    refetchOnMount: "always",
  });

export const useExperimentsList = (
  variables: ApiTypes.Experiments["RequestParams"]["getExperiments"]
) =>
  useQuery({
    queryKey: [QUERY_KEYS_EXPERIMENT.getExperimentsList],
    queryFn: () => getExperimentsList(variables),
    cacheTime: 0,
  });

export const usePlantsTypeList = () =>
  useQuery({
    queryKey: [QUERY_KEYS_STATICS.getPlantsTypeList],
    queryFn: () => getPlantsTypeList(),
  });

export const useProductsTypeList = (
  variables: ApiTypes.Products["RequestParams"]["getProducts"]
) =>
  useQuery({
    queryKey: [QUERY_KEYS_PRODUCTS.getProductsTypeList],
    queryFn: () => getProductsTypeList(variables),
  });

export const useCreateCroppingPattern = (
  handleOnSuccess: (data: any) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      variables: ApiTypes.CroppingPattern["RequestParams"]["create"]
    ) => createCroppingPattern(variables),
    onSuccess: (data) => {
      handleOnSuccess(data);
      toast.success(" با موفقیت ثبت شد");
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS_CROPPING_PATTERN.getCroppingPatterns],
      });
    },
    onError: () => {
      toast.error("خطا در ثبت الگوی کشت");
    },
  });
};

export const useCroppingSuitabilities = (
  variables: ApiTypes.CroppingSuitability["RequestParams"]["getAll"]
) =>
  useQuery({
    queryKey: [QUERY_KEYS_CROPPING_SUITABILITY.getCroppingSuitabilities],
    queryFn: () => getCroppingSuitabilities(variables),
    refetchOnMount: "always",
  });

export const useCreateCroppingSuitabilities = (
  handleOnSuccess: (data: any) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      variables: ApiTypes.CroppingSuitability["RequestParams"]["create"]
    ) => createCroppingSuitabilities(variables),
    onSuccess: (data) => {
      handleOnSuccess(data);
      toast.success(" با موفقیت ثبت شد");
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS_CROPPING_SUITABILITY.getCroppingSuitabilities],
      });
    },
    onError: (e) => {
      console.log(e);
      toast.error("خطا در ثبت تناسب کشت");
    },
  });
};

export const useCroppingPattern = (
  variables: ApiTypes.CroppingPattern["RequestParams"]["getOne"]
) =>
  useQuery({
    queryKey: croppingPatternQueryKeys.croppingPattern(variables.pathParams),
    queryFn: () => getCroppingPattern(variables),
    refetchInterval: (data) => (data?.reports.length ? false : 10000),
  });

export const useCroppingSuitability = (
  variables: ApiTypes.CroppingSuitability["RequestParams"]["getOne"]
) =>
  useQuery({
    queryKey: croppingSuitabilityQueryKeys.croppingSuitability(
      variables.pathParams
    ),
    queryFn: () => getCroppingSuitability(variables),
    refetchInterval: (data) => (data?.reports.length ? false : 10000),
  });

export const useIrrigationSource = (
  variables: ApiTypes.IrrigationSource["RequestParams"]["getOne"]
) =>
  useQuery({
    queryKey: irrigationSourceQueryKeys.irrigationSource(variables.pathParams),
    queryFn: () => getIrrigationSource(variables),
  });

export const usePostIrrigationSourceDeviceCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [QUERY_KEYS_IRIIGATION_SOURCE.postTrackerDeviceCode],
    mutationFn: (params: ApiTypes.IrrigationSourceDeviceCode["RequestParams"]["create"]) =>
      postIrrigationSourceDeviceCode(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS_IRIIGATION_SOURCE.getIrrigationSource],
      });
    },
  });
};

export const useUpdateIrrigationSourceDeviceCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [QUERY_KEYS_IRIIGATION_SOURCE.updateTrackerDeviceCode],
    mutationFn: (params: ApiTypes.IrrigationSourceDeviceCode["RequestParams"]["update"]) =>
      updateIrrigationSourceDeviceCode(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS_IRIIGATION_SOURCE.getIrrigationSource],
      });
    },
  });
};

export const useDeleteIrrigationSourceDeviceCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [QUERY_KEYS_IRIIGATION_SOURCE.deleteTrackerDeviceCode],
    mutationFn: (params: ApiTypes.IrrigationSourceDeviceCode["RequestParams"]["delete"]) =>
      deleteIrrigationSourceDeviceCode(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS_IRIIGATION_SOURCE.getIrrigationSource],
      });
    },
  });
};

export const useIrrigationSourceSensors = (
  params: ApiTypes.IrrigationSourceSensor["RequestParams"]["getSensors"],
  enabled: boolean
) =>
  useQuery({
    queryKey: irrigationSourceQueryKeys.sensors(params),
    queryFn: () => getIrrigationSensors(params),
    enabled,
  });

export const useEngineVibrationLevelChanges = (
  params: ApiTypes.IrrigationSourceSensor["RequestParams"]["getEngineVibrationLevelChanges"]
) =>
  useQuery({
    queryKey: irrigationSourceQueryKeys.engineVibrationLevelChanges(params),
    queryFn: () => getEngineVibrationLevelChanges(params),
  });

export const useStatePowerChanges = (
  params: ApiTypes.IrrigationSourceSensor["RequestParams"]["getStatePowerChanges"],
) =>
  useQuery({
    queryKey: irrigationSourceQueryKeys.statePowerChanges(params),
    queryFn: () => getStatePowerChanges(params),
  });

export const useStatePumpChanges = (
  params: ApiTypes.IrrigationSourceSensor["RequestParams"]["getStatePumpChanges"],
) =>
  useQuery({
    queryKey: irrigationSourceQueryKeys.statePumpChanges(params),
    queryFn: () => getStatePumpChanges(params),
  });

export const useConsumptionAmpsChanges = (
  params: ApiTypes.IrrigationSourceSensor["RequestParams"]["getConsumptionAmpsChanges"],
) =>
  useQuery({
    queryKey: irrigationSourceQueryKeys.consumptionAmpsChanges(params),
    queryFn: () => getConsumptionApmsChanges(params),
  });